/**
 * Twist chute (M16) — the reorient run's corkscrew, built to be
 * INDISTINGUISHABLE from the kit channel (user goal): the cross-section
 * is not authored here at all — it is SLICED from the kit `straight`
 * piece itself (plane z=0 in piece-local space), every slice segment
 * keeping its interpolated UVs into the kit colormap, and the extrusion
 * reuses the kit's SHARED materials. Palette variations and chapter
 * pipe recolors therefore apply exactly like any placed piece, and a 0°
 * twist is the same surface as the piece it replaces (the demo's A/B).
 *
 * Segments extrude INDEPENDENTLY (no vertex welding): the kit look is
 * flat swatch bands with hard edges, and non-indexed triangles +
 * computeVertexNormals reproduce the flat shading. No end caps — the
 * chute always sits flush between kit pieces.
 */
import * as THREE from "three";
import type { Assets } from "./assets.ts";

/** One sliced cross-section segment: endpoints in piece-local (x, y)
 * with their colormap UVs, oriented so (b − a) × extrusion points OUT. */
export interface SectionSeg {
  ax: number;
  ay: number;
  au: number;
  av: number;
  bx: number;
  by: number;
  bu: number;
  bv: number;
  mat: number;
}

export interface ChannelSection {
  segs: SectionSeg[];
  /** Shared kit materials, indexed by SectionSeg.mat. */
  materials: THREE.Material[];
  /** Piece-local y of the channel floor (the rail line's height). */
  floorY: number;
}

let cached: ChannelSection | null = null;

/**
 * Slice the kit `straight` piece at its mid-plane. Piece-local frame:
 * channel runs along z, lateral is x, up is y; the channel floor sits at
 * local y ≈ 0.25 (the measured kit fact) — recomputed here from the
 * slice so the chute needs no hardcoded numbers.
 */
export function extractChannelSection(assets: Assets): ChannelSection | null {
  if (cached) return cached;
  const piece = assets.getPiece("straight");
  if (!piece) return null;
  piece.updateMatrixWorld(true);
  const segs: SectionSeg[] = [];
  const materials: THREE.Material[] = [];
  const matIndexOf = (m: THREE.Material): number => {
    let i = materials.indexOf(m);
    if (i < 0) {
      i = materials.length;
      materials.push(m);
    }
    return i;
  };
  const va = new THREE.Vector3();
  const vb = new THREE.Vector3();
  const vc = new THREE.Vector3();
  piece.traverse((o: THREE.Object3D) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    const geo = mesh.geometry as THREE.BufferGeometry;
    const pos = geo.getAttribute("position");
    const uv = geo.getAttribute("uv");
    const index = geo.getIndex();
    const triCount = (index ? index.count : pos.count) / 3;
    const groups = geo.groups.length > 0 ? geo.groups : [{
      start: 0,
      count: index ? index.count : pos.count,
      materialIndex: 0,
    }];
    const matOf = (tri: number): THREE.Material => {
      const at = tri * 3;
      for (const g of groups) {
        if (at >= g.start && at < g.start + g.count) {
          const mats = Array.isArray(mesh.material)
            ? mesh.material
            : [mesh.material];
          return mats[g.materialIndex ?? 0] ?? mats[0];
        }
      }
      return Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;
    };
    const read = (tri: number, corner: number, out: THREE.Vector3): number => {
      const i = index ? index.getX(tri * 3 + corner) : tri * 3 + corner;
      out.fromBufferAttribute(pos as THREE.BufferAttribute, i);
      mesh.localToWorld(out);
      return i;
    };
    for (let t = 0; t < triCount; t++) {
      const ia = read(t, 0, va);
      const ib = read(t, 1, vb);
      const ic = read(t, 2, vc);
      const z = [va.z, vb.z, vc.z];
      const pts = [va, vb, vc];
      const idxs = [ia, ib, ic];
      // Edges crossing z = 0 → up to one slice segment per triangle.
      const hits: { x: number; y: number; u: number; v: number }[] = [];
      for (let e = 0; e < 3; e++) {
        const f = (e + 1) % 3;
        if ((z[e] < 0) === (z[f] < 0)) continue;
        const k = z[e] / (z[e] - z[f]);
        hits.push({
          x: pts[e].x + (pts[f].x - pts[e].x) * k,
          y: pts[e].y + (pts[f].y - pts[e].y) * k,
          u: uv.getX(idxs[e]) + (uv.getX(idxs[f]) - uv.getX(idxs[e])) * k,
          v: uv.getY(idxs[e]) + (uv.getY(idxs[f]) - uv.getY(idxs[e])) * k,
        });
      }
      if (hits.length !== 2) continue;
      let [a, b] = hits;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      if (Math.hypot(dx, dy) < 1e-5) continue;
      // Orient so the extruded quad's outward normal ((b−a) × +z ∝
      // (dy, −dx)) matches the source triangle's facing.
      const e1 = vb.clone().sub(va);
      const e2 = vc.clone().sub(va);
      const n = e1.cross(e2);
      if (dy * n.x - dx * n.y < 0) [a, b] = [b, a];
      segs.push({
        ax: a.x,
        ay: a.y,
        au: a.u,
        av: a.v,
        bx: b.x,
        by: b.y,
        bu: b.u,
        bv: b.v,
        mat: matIndexOf(matOf(t)),
      });
    }
  });
  if (segs.length === 0) return null;
  // Subdivide wide slice segments: a twisted quad is NON-planar — flat
  // per-triangle normals over a 1.5-unit floor band read as a dark
  // checkerboard mid-roll (bench finding). Short bands + smooth-along-
  // strip normals keep the roll continuous while straight runs stay
  // visually flat (coplanar faces share identical normals anyway).
  const MAX_W = 0.16;
  const fine: SectionSeg[] = [];
  for (const s of segs) {
    const w = Math.hypot(s.bx - s.ax, s.by - s.ay);
    const n = Math.max(1, Math.ceil(w / MAX_W));
    for (let i = 0; i < n; i++) {
      const t0 = i / n;
      const t1 = (i + 1) / n;
      fine.push({
        ax: s.ax + (s.bx - s.ax) * t0,
        ay: s.ay + (s.by - s.ay) * t0,
        au: s.au + (s.bu - s.au) * t0,
        av: s.av + (s.bv - s.av) * t0,
        bx: s.ax + (s.bx - s.ax) * t1,
        by: s.ay + (s.by - s.ay) * t1,
        bu: s.au + (s.bu - s.au) * t1,
        bv: s.av + (s.bv - s.av) * t1,
        mat: s.mat,
      });
    }
  }
  segs.length = 0;
  segs.push(...fine);
  // Channel floor: the lowest slice point near the centerline (|x| small)
  // that has surface ABOVE it is the floor line the rail rides — in
  // practice the minimum y among near-center points of upward-facing
  // segments. The kit fact says 0.25; measure it instead of trusting it.
  let floorY = Infinity;
  for (const s of segs) {
    for (const [x, y] of [[s.ax, s.ay], [s.bx, s.by]] as const) {
      if (Math.abs(x) < 0.2 && y > 0.05) floorY = Math.min(floorY, y);
    }
  }
  if (!Number.isFinite(floorY)) floorY = 0.25;
  cached = { segs, materials, floorY };
  return cached;
}

/** Station placement callback: writes the world position of a section
 * point (piece-local lateral x, up y) at station `i` into `out`. */
export type SectionPlacer = (
  i: number,
  x: number,
  y: number,
  out: THREE.Vector3,
) => void;

/**
 * Extrude the sliced section along `count` stations. Each section
 * segment becomes its own INDEXED strip — vertices shared along the
 * extrusion (smooth normals through the roll; coplanar straight runs
 * still shade flat) but never across segments (hard swatch-band edges,
 * the kit look); winding and UVs come straight from the slice.
 */
export function buildChuteMesh(
  section: ChannelSection,
  count: number,
  place: SectionPlacer,
): THREE.Mesh {
  const segsByMat = new Map<number, SectionSeg[]>();
  for (const s of section.segs) {
    const list = segsByMat.get(s.mat) ?? [];
    list.push(s);
    segsByMat.set(s.mat, list);
  }
  const vertsPerStrip = count * 2;
  const totalVerts = section.segs.length * vertsPerStrip;
  const positions = new Float32Array(totalVerts * 3);
  const uvs = new Float32Array(totalVerts * 2);
  const indices: number[] = [];
  const p = new THREE.Vector3();
  let vi = 0;
  const geo = new THREE.BufferGeometry();
  const matsUsed: THREE.Material[] = [];
  for (const [mat, segs] of segsByMat) {
    const start = indices.length;
    for (const s of segs) {
      const base = vi;
      for (let i = 0; i < count; i++) {
        place(i, s.ax, s.ay, p);
        positions[vi * 3] = p.x;
        positions[vi * 3 + 1] = p.y;
        positions[vi * 3 + 2] = p.z;
        uvs[vi * 2] = s.au;
        uvs[vi * 2 + 1] = s.av;
        vi++;
        place(i, s.bx, s.by, p);
        positions[vi * 3] = p.x;
        positions[vi * 3 + 1] = p.y;
        positions[vi * 3 + 2] = p.z;
        uvs[vi * 2] = s.bu;
        uvs[vi * 2 + 1] = s.bv;
        vi++;
      }
      for (let i = 0; i < count - 1; i++) {
        const a0 = base + i * 2;
        const b0 = a0 + 1;
        const a1 = a0 + 2;
        const b1 = a0 + 3;
        // CCW with the outward normal encoded by the slice's a→b order.
        indices.push(a0, b0, a1, a1, b0, b1);
      }
    }
    geo.addGroup(start, indices.length - start, matsUsed.length);
    matsUsed.push(section.materials[mat]);
  }
  geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geo.setAttribute("uv", new THREE.BufferAttribute(uvs, 2));
  geo.setIndex(indices);
  geo.computeVertexNormals(); // shared along strips → smooth roll
  const mesh = new THREE.Mesh(geo, matsUsed);
  mesh.name = "twist-chute";
  return mesh;
}
